import { useQuery } from "@apollo/client";
import Delete from "@mui/icons-material/Delete";
import { Box, Button, Tooltip, Typography } from "@mui/material";
import { GridActionsCellItem, GridSortModel } from "@mui/x-data-grid-premium";
import { DataGrid } from "components/DataGrid";
import { getAllocatedFeeForIndex } from "encamp-shared/src/utils/allocateFee";
import {
  AssociatedFacilitiesQuery,
  AssociatedTierIiReportsQuery,
  AssociatedSubmissionsQuery,
} from "generated-graphql/graphql";
import { usePaginationModel } from "hooks/usePaginationModel";
import pluralize from "pluralize";
import { type ReactNode, useCallback, useMemo, useState } from "react";
import { Control, useFieldArray, useWatch } from "react-hook-form";

import { AddAssociation } from "./AddAssociation";
import { findAssociationIndex } from "./lib/associationUtils";
import { safeParseFloat } from "./lib/numberUtils";
import {
  FACILITY_ASSOCIATIONS_QUERY,
  TIER_II_REPORT_ASSOCIATIONS_QUERY,
  SUBMISSION_ASSOCIATIONS_QUERY,
} from "./lib/schema";
import {
  Association,
  FacilityAssociation,
  ReportAssociation,
  SubmissionAssociation,
  PayeeFormInputs,
} from "./lib/types";

const AssociatedEntities = ({
  control,
  tenantId,
  type,
  disabled,
  hideTitle = false,
  titleOverride,
  transactionFee,
  addDisabled,
  addDisabledReason,
}: {
  control: Control<PayeeFormInputs>;
  tenantId: string;
  type: "Report" | "Facility" | "Submission";
  disabled?: boolean;
  hideTitle?: boolean;
  titleOverride?: string;
  transactionFee?: number;
  addDisabled?: boolean;
  addDisabledReason?: ReactNode;
}) => {
  const [showAddModal, setShowAddModal] = useState(false);
  const { update, remove, append } = useFieldArray({
    control,
    name: "associations",
  });
  const associations = useWatch({ control, name: "associations" });

  // Get associations with non-zero amounts for fee allocation
  const associationsWithAmount = useMemo(
    () => associations.filter((a: Association) => a.amount > 0),
    [associations]
  );

  // Calculate per-item transaction fee using lossless allocation
  // Uses "largest remainder" method to ensure no cents are lost due to rounding
  // Example: $1.00 / 3 = [$0.34, $0.33, $0.33] = $1.00 (no loss)
  const getPerItemTransactionFee = useCallback(
    (association: Association): number => {
      if (!transactionFee || association.amount <= 0) return 0;
      const index = associationsWithAmount.findIndex(
        (a: Association) =>
          (a.facilityId && a.facilityId === association.facilityId) ||
          (a.reportId && a.reportId === association.reportId) ||
          (a.submissionId && a.submissionId === association.submissionId)
      );
      if (index < 0) return 0;
      return getAllocatedFeeForIndex(transactionFee, associationsWithAmount.length, index);
    },
    [transactionFee, associationsWithAmount]
  );

  const updateAssociation = (association: Association) => {
    const index = findAssociationIndex(associations, association);
    if (index >= 0) {
      update(index, association);
    }
  };

  const effectiveAddDisabled = disabled || addDisabled || !tenantId;

  const addDisabledTooltipTitle = disabled
    ? "Associations are locked while this payment is not editable."
    : !tenantId
      ? "Select an organization to add associations."
      : addDisabledReason;

  return (
    <Box>
      {showAddModal && (
        <AddAssociation
          onClose={() => setShowAddModal(false)}
          addAssociation={append}
          disabledFacilityIds={associations.map((a: Association) => a.facilityId ?? "")}
          disabledReportIds={associations.map((a: Association) => a.reportId ?? "")}
          disabledSubmissionIds={associations.map((a: Association) => a.submissionId ?? "")}
          tenantId={tenantId}
          type={type}
        />
      )}
      <Box
        sx={{
          justifyContent: hideTitle ? "flex-end" : "space-between",
          display: "flex",
          alignItems: "center",
          mb: 1,
        }}
      >
        {!hideTitle && (
          <Typography variant="h6">
            {titleOverride || `Associated ${pluralize(type, 2)}`}
          </Typography>
        )}

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <Tooltip
            title={addDisabledTooltipTitle ?? ""}
            arrow
            disableHoverListener={!addDisabledTooltipTitle}
          >
            <span>
              <Button
                variant="outlined"
                onClick={() => setShowAddModal(true)}
                disabled={effectiveAddDisabled}
              >
                Add {type}
              </Button>
            </span>
          </Tooltip>
        </Box>
      </Box>
      {type === "Report" && (
        <ReportTable
          associations={associations
            .filter((a: Association) => a.reportId?.length)
            .map((a: Association) => a as ReportAssociation)}
          deleteReport={(reportId) => {
            const index = findAssociationIndex(associations, { reportId });
            if (index >= 0) remove(index);
          }}
          processRowUpdate={(reportAssociation) => updateAssociation(reportAssociation)}
          disabled={disabled}
          getTransactionFee={getPerItemTransactionFee}
        />
      )}
      {type === "Facility" && (
        <FacilityTable
          associations={associations
            .filter((a: Association) => a.facilityId?.length)
            .map((a: Association) => a as FacilityAssociation)}
          deleteFacility={(facilityId) => {
            const index = findAssociationIndex(associations, { facilityId });
            if (index >= 0) remove(index);
          }}
          processRowUpdate={(facilityAssociation) => updateAssociation(facilityAssociation)}
          disabled={disabled}
          getTransactionFee={getPerItemTransactionFee}
        />
      )}
      {type === "Submission" && (
        <SubmissionTable
          associations={associations
            .filter((a: Association) => a.submissionId?.length)
            .map((a: Association) => a as SubmissionAssociation)}
          tenantId={tenantId}
          deleteSubmission={(submissionId) => {
            const index = findAssociationIndex(associations, { submissionId });
            if (index >= 0) remove(index);
          }}
          processRowUpdate={(submissionAssociation) => updateAssociation(submissionAssociation)}
          disabled={disabled}
          getTransactionFee={getPerItemTransactionFee}
        />
      )}
    </Box>
  );
};

type ReportRow = AssociatedTierIiReportsQuery["tierIIReports"]["items"][number] & {
  amount: number;
  transactionFeeAllocation: number;
};

const ReportTable = ({
  associations,
  deleteReport,
  processRowUpdate,
  disabled,
  getTransactionFee,
}: {
  associations: ReportAssociation[];
  deleteReport: (reportId: string) => void;
  processRowUpdate: (reportAssociation: ReportAssociation) => void;
  disabled?: boolean;
  getTransactionFee: (association: Association) => number;
}) => {
  const [paginationModel, setPaginationModel] = usePaginationModel();
  const [sortModel, setSortModel] = useState<GridSortModel>([]);
  const reportIds: string[] = associations.map((a) => a.reportId);

  // Skip query only if no reportIds
  const shouldSkipQuery = !reportIds.length;

  const {
    data: reports,
    previousData: previousReports,
    loading,
  } = useQuery(TIER_II_REPORT_ASSOCIATIONS_QUERY, {
    variables: {
      search: `${reportIds.map((fid) => `id:${fid}`).join(" or ")}`,
      sort: [...sortModel],
      page: paginationModel.page,
      pageSize: paginationModel.pageSize,
    },
    skip: shouldSkipQuery,
  });

  const rows: ReportRow[] =
    (reports ?? previousReports)?.tierIIReports.items
      .filter((r) => associations.some((a) => a.reportId === r.id))
      .map((r) => {
        const association = associations.find((a) => a.reportId === r.id);
        const amount = association?.amount ?? 0;
        return {
          ...r,
          amount,
          // Only allocate transaction fee if this association has a non-zero amount
          transactionFeeAllocation: association ? getTransactionFee(association) : 0,
        };
      }) ?? [];

  // Show loading if we have associations but no report data yet
  const isLoadingReportData = reportIds.length > 0 && loading && rows.length === 0;

  // Don't render the DataGrid until we have data or are done loading
  if (isLoadingReportData) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Loading report data...
        </Typography>
      </Box>
    );
  }

  return (
    <DataGrid<ReportRow>
      columns={[
        {
          headerName: "Facility",
          field: "facility.name",
          flex: 1,
          valueGetter: (_, row) => row.facility.name,
        },
        {
          headerName: "Facility Address",
          field: "facilityAddress",
          sortable: false,
          flex: 1.2,
          valueGetter: (_, row) => {
            const address = row.facility.streetAddress1;
            if (address) {
              const { streetAddress1, city, state, zip } = row.facility;
              return `${streetAddress1}, ${city}, ${state} ${zip}`;
            }
            return "";
          },
        },
        {
          headerName: "Year",
          field: "reportingYear",
          valueGetter: (_, row) => row.reportingYear,
          flex: 0.4,
        },
        {
          headerName: "Type",
          field: "reportKind",
          valueGetter: (_, row) => row.reportKind,
          flex: 0.4,
        },
        {
          headerName: "Invoice Amount",
          field: "amount",
          flex: 0.6,
          editable: !disabled,
          valueFormatter(value) {
            return new Intl.NumberFormat("en-US", {
              style: "currency",
              currency: "USD",
            }).format(value);
          },
          sortable: false,
        },
        {
          headerName: "Transaction Fee",
          field: "transactionFeeAllocation",
          flex: 0.6,
          valueFormatter(value) {
            return new Intl.NumberFormat("en-US", {
              style: "currency",
              currency: "USD",
            }).format(value);
          },
          sortable: false,
        },
        {
          headerName: "Total Amount",
          field: "totalAmount",
          flex: 0.6,
          valueGetter: (_, row) => row.amount + row.transactionFeeAllocation,
          valueFormatter(value) {
            return new Intl.NumberFormat("en-US", {
              style: "currency",
              currency: "USD",
            }).format(value);
          },
          sortable: false,
        },
        {
          field: "actions",
          type: "actions",
          getActions: (params) => [
            <GridActionsCellItem
              key="delete"
              icon={<Delete />}
              label="Delete"
              onClick={() => deleteReport(params.row.id)}
              disabled={disabled}
            />,
          ],
        },
      ]}
      loading={loading}
      rows={rows}
      rowCount={reports?.tierIIReports.count ?? previousReports?.tierIIReports.count ?? 0}
      processRowUpdate={(newRow) => {
        // Convert string amount to number using safe parsing that handles commas
        const numericAmount = safeParseFloat(String(newRow.amount)) ?? 0;
        processRowUpdate({ reportId: newRow.id, amount: numericAmount });
        return { ...newRow, amount: numericAmount };
      }}
      sortModel={sortModel}
      onSortModelChange={setSortModel}
      pagination
      paginationMode="server"
      paginationModel={paginationModel}
      onPaginationModelChange={setPaginationModel}
      pageSizeOptions={[10, 25, 50, 100]}
    />
  );
};

type FacilityRow = AssociatedFacilitiesQuery["facilities"]["items"][number] & {
  amount: number;
  transactionFeeAllocation: number;
};

const FacilityTable = ({
  associations,
  deleteFacility,
  processRowUpdate,
  disabled,
  getTransactionFee,
}: {
  associations: FacilityAssociation[];
  deleteFacility: (facilityId: string) => void;
  processRowUpdate: (facilityAssociation: FacilityAssociation) => void;
  disabled?: boolean;
  getTransactionFee: (association: Association) => number;
}) => {
  const [paginationModel, setPaginationModel] = usePaginationModel();
  const [sortModel, setSortModel] = useState<GridSortModel>([]);
  const facilityIds: string[] = useMemo(
    () => associations.map((a) => a.facilityId),
    [associations]
  );
  const {
    data: facilities,
    previousData: previousFacilities,
    loading: facilitiesLoading,
  } = useQuery(FACILITY_ASSOCIATIONS_QUERY, {
    variables: {
      search: `${facilityIds.map((fid) => `id:${fid}`).join(" or ")}`,
      sort: [...sortModel],
      page: paginationModel.page,
      pageSize: paginationModel.pageSize,
    },
    skip: !facilityIds.length,
  });

  const rows: FacilityRow[] =
    (facilities ?? previousFacilities)?.facilities.items
      .filter((f) => associations.some((a) => a.facilityId === f.id))
      .map((f) => {
        const association = associations.find((a) => a.facilityId === f.id);
        const amount = association?.amount ?? 0;
        return {
          ...f,
          amount,
          // Only allocate transaction fee if this association has a non-zero amount
          transactionFeeAllocation: association ? getTransactionFee(association) : 0,
        };
      }) ?? [];

  return (
    <DataGrid<FacilityRow>
      columns={[
        {
          headerName: "Facility",
          field: "name",
          flex: 1,
          valueGetter: (_, row) => row.name,
        },
        {
          headerName: "Facility ID",
          field: "customerFacilityId",
          flex: 0.7,
          valueGetter: (_, row) => row.customerFacilityId ?? "",
        },
        {
          headerName: "Facility Address",
          field: "facilityAddress",
          sortable: false,
          flex: 1,
          valueGetter: (_, row) => {
            const facility = row;
            const address = facility?.streetAddress1;
            if (address) {
              const { streetAddress1, city, state, zip } = facility;
              return `${streetAddress1}, ${city}, ${state} ${zip}`;
            }
            return "";
          },
        },
        {
          headerName: "Invoice Amount",
          field: "amount",
          flex: 0.7,
          editable: !disabled,
          valueFormatter(value) {
            return new Intl.NumberFormat("en-US", {
              style: "currency",
              currency: "USD",
            }).format(value);
          },
          sortable: false,
        },
        {
          headerName: "Transaction Fee",
          field: "transactionFeeAllocation",
          flex: 0.7,
          valueFormatter(value) {
            return new Intl.NumberFormat("en-US", {
              style: "currency",
              currency: "USD",
            }).format(value);
          },
          sortable: false,
        },
        {
          headerName: "Total Amount",
          field: "totalAmount",
          flex: 0.7,
          valueGetter: (_, row) => row.amount + row.transactionFeeAllocation,
          valueFormatter(value) {
            return new Intl.NumberFormat("en-US", {
              style: "currency",
              currency: "USD",
            }).format(value);
          },
          sortable: false,
        },
        {
          field: "actions",
          type: "actions",
          getActions: (params) => [
            <GridActionsCellItem
              key="delete"
              icon={<Delete />}
              label="Delete"
              onClick={() => deleteFacility(params.row.id)}
              disabled={disabled}
            />,
          ],
        },
      ]}
      loading={facilitiesLoading}
      rows={rows}
      rowCount={facilities?.facilities.count ?? 0}
      processRowUpdate={(newRow) => {
        // Convert string amount to number using safe parsing that handles commas
        const numericAmount = safeParseFloat(String(newRow.amount)) ?? 0;
        processRowUpdate({ facilityId: newRow.id, amount: numericAmount });
        return { ...newRow, amount: numericAmount };
      }}
      sortModel={sortModel}
      onSortModelChange={setSortModel}
      pagination
      paginationMode="server"
      paginationModel={paginationModel}
      onPaginationModelChange={setPaginationModel}
    />
  );
};

type VirtualEventItem = AssociatedSubmissionsQuery["submissionVirtualEvents"]["items"][number];

type SubmissionRow = {
  id: string; // submission id
  title: string | null | undefined;
  submissionType: string;
  status: string;
  dueDate: string | null;
  facilityName: string;
  amount: number;
  transactionFeeAllocation: number;
};

const SubmissionTable = ({
  associations,
  tenantId,
  deleteSubmission,
  processRowUpdate,
  disabled,
  getTransactionFee,
}: {
  associations: SubmissionAssociation[];
  tenantId: string;
  deleteSubmission: (submissionId: string) => void;
  processRowUpdate: (submissionAssociation: SubmissionAssociation) => void;
  disabled?: boolean;
  getTransactionFee: (association: Association) => number;
}) => {
  const [paginationModel, setPaginationModel] = usePaginationModel();
  const [sortModel, setSortModel] = useState<GridSortModel>([]);
  const submissionIds: string[] = useMemo(
    () => associations.map((a) => a.submissionId),
    [associations]
  );
  const submissionSearch = useMemo(() => {
    if (!submissionIds.length || !tenantId) return "";
    const idsFilter = submissionIds.join("|");
    return `tenantId:${tenantId} submissionId:${idsFilter}`;
  }, [submissionIds, tenantId]);

  const {
    data: virtualEvents,
    previousData: previousVirtualEvents,
    loading: submissionsLoading,
  } = useQuery(SUBMISSION_ASSOCIATIONS_QUERY, {
    variables: {
      search: submissionSearch,
      sort: [...sortModel],
      page: paginationModel.page,
      pageSize: paginationModel.pageSize,
    },
    skip: !submissionIds.length || !tenantId,
  });

  const rows: SubmissionRow[] = useMemo(() => {
    const items = (virtualEvents ?? previousVirtualEvents)?.submissionVirtualEvents.items ?? [];
    return items
      .filter(
        (ve: VirtualEventItem) =>
          ve.submission && associations.some((a) => a.submissionId === ve.submission?.id)
      )
      .map((ve: VirtualEventItem) => {
        const association = associations.find((a) => a.submissionId === ve.submission?.id);
        const amount = association?.amount ?? 0;
        return {
          id: ve.submission?.id ?? "",
          title: ve.submission?.title ?? ve.title,
          submissionType: ve.submission?.submissionType ?? "",
          status: ve.submission?.status ?? "",
          dueDate: ve.submission?.dueDate ?? ve.deadline,
          facilityName: ve.facilities?.[0]?.name ?? "",
          amount,
          // Only allocate transaction fee if this association has a non-zero amount
          transactionFeeAllocation: association ? getTransactionFee(association) : 0,
        };
      });
  }, [virtualEvents, previousVirtualEvents, associations, getTransactionFee]);

  return (
    <DataGrid<SubmissionRow>
      columns={[
        {
          headerName: "Title",
          field: "title",
          flex: 1.2,
        },
        {
          headerName: "Type",
          field: "submissionType",
          flex: 0.5,
        },
        {
          headerName: "Facility",
          field: "facilityName",
          flex: 1,
          sortable: false,
        },
        {
          headerName: "Due Date",
          field: "dueDate",
          flex: 0.6,
          valueFormatter: (value) => {
            if (!value) return "";
            const date = new Date(value);
            return date.toLocaleDateString("en-US", {
              year: "numeric",
              month: "short",
              day: "numeric",
            });
          },
        },
        {
          headerName: "Invoice Amount",
          field: "amount",
          flex: 0.6,
          editable: !disabled,
          valueFormatter(value) {
            return new Intl.NumberFormat("en-US", {
              style: "currency",
              currency: "USD",
            }).format(value);
          },
          sortable: false,
        },
        {
          headerName: "Transaction Fee",
          field: "transactionFeeAllocation",
          flex: 0.6,
          valueFormatter(value) {
            return new Intl.NumberFormat("en-US", {
              style: "currency",
              currency: "USD",
            }).format(value);
          },
          sortable: false,
        },
        {
          headerName: "Total Amount",
          field: "totalAmount",
          flex: 0.6,
          valueGetter: (_, row) => row.amount + row.transactionFeeAllocation,
          valueFormatter(value) {
            return new Intl.NumberFormat("en-US", {
              style: "currency",
              currency: "USD",
            }).format(value);
          },
          sortable: false,
        },
        {
          field: "actions",
          type: "actions",
          getActions: (params) => [
            <GridActionsCellItem
              key="delete"
              icon={<Delete />}
              label="Delete"
              onClick={() => deleteSubmission(params.row.id)}
              disabled={disabled}
            />,
          ],
        },
      ]}
      loading={submissionsLoading}
      rows={rows}
      rowCount={virtualEvents?.submissionVirtualEvents.count ?? 0}
      processRowUpdate={(newRow) => {
        // Convert string amount to number using safe parsing that handles commas
        const numericAmount = safeParseFloat(String(newRow.amount)) ?? 0;
        processRowUpdate({ submissionId: newRow.id, amount: numericAmount });
        return { ...newRow, amount: numericAmount };
      }}
      sortModel={sortModel}
      onSortModelChange={setSortModel}
      pagination
      paginationMode="server"
      paginationModel={paginationModel}
      onPaginationModelChange={setPaginationModel}
    />
  );
};

export { AssociatedEntities };
